import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";

/** Shared layout & surface styles for Sales / Scoping pages */
export const salesTokens = {
  page: "min-h-full bg-[#f4f6fb] text-slate-800 font-sans antialiased",
  container: "max-w-[1400px] mx-auto px-4 sm:px-5 py-4 space-y-4",
  panel:
    "rounded-xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(8,21,73,0.04),0_4px_12px_-2px_rgba(8,21,73,0.06)]",
  panelHeader:
    "px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3",
  panelTitle: "text-[11px] font-semibold uppercase tracking-wide text-slate-500",
  statCard:
    "group relative overflow-hidden rounded-xl border border-slate-200/70 bg-white p-3 shadow-[0_1px_2px_rgba(8,21,73,0.04)] transition hover:border-ctb-blue/20 hover:shadow-sm",
  statAccent: "absolute left-0 top-0 bottom-0 w-0.5 bg-ctb-blue/80",
  tableWrap: "overflow-x-auto -mx-1",
  table: "w-full text-left text-[11px] border-collapse",
  thead: "border-b border-slate-200 text-slate-500 text-[10px] font-semibold uppercase tracking-wide",
  th: "py-2 px-3 font-semibold",
  tr: "border-b border-slate-100/90 hover:bg-slate-50/80 transition-colors",
  td: "py-2.5 px-3",
  input:
    "w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-ctb-blue focus:ring-2 focus:ring-ctb-blue/10 transition",
  tabActive: "text-ctb-blue border-b-2 border-ctb-blue font-semibold",
  tabIdle: "text-slate-500 hover:text-slate-700 border-b-2 border-transparent font-medium",
} as const;

export type SalesStatus = "DRAFT" | "SUBMITTED" | "CONVERTED" | string;

const statusStyles: Record<string, string> = {
  DRAFT: "bg-amber-50 text-amber-800 border-amber-200/80",
  SUBMITTED: "bg-ctb-blue-0/40 text-ctb-blue border-ctb-blue-200/60",
  CONVERTED: "bg-emerald-50 text-emerald-800 border-emerald-200/80",
  REJECTED: "bg-rose-50 text-rose-800 border-rose-200/80",
  EXPIRED: "bg-rose-50 text-rose-800 border-rose-200/80",
  LOST: "bg-slate-100 text-slate-800 border-slate-300",
};

export const SalesPageShell: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = ""
}) => (
  <div className={`${salesTokens.page} ${className}`}>
    <div className={salesTokens.container}>{children}</div>
  </div>
);

export const SalesPageHeader: React.FC<{
  eyebrow: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
}> = ({ eyebrow, title, description, actions }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between pb-1">
    <div className="space-y-1 min-w-0">
      <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-ctb-blue bg-ctb-blue/5 border border-ctb-blue/10 px-2 py-0.5 rounded-md">
        {eyebrow}
      </span>
      <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-[1.35rem]">{title}</h1>
      {description && (
        <p className="text-[11px] text-slate-500 max-w-xl leading-relaxed">{description}</p>
      )}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

export const SalesStatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  accent?: "default" | "amber" | "blue" | "emerald";
}> = ({ label, value, accent = "default" }) => {
  const accentColor =
    accent === "amber"
      ? "bg-amber-500"
      : accent === "blue"
        ? "bg-ctb-blue-600"
        : accent === "emerald"
          ? "bg-emerald-500"
          : "bg-ctb-blue";
  return (
    <div className={salesTokens.statCard}>
      <div className={`${salesTokens.statAccent} ${accentColor}`} />
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 pl-1">{label}</span>
      <div className="text-xl font-bold text-slate-900 mt-1 pl-1 tabular-nums">{value}</div>
    </div>
  );
};

export const SalesPanel: React.FC<{
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}> = ({ title, action, children, className = "", bodyClassName = "p-4" }) => (
  <div className={`${salesTokens.panel} ${className}`}>
    {title && (
      <div className={salesTokens.panelHeader}>
        <h2 className={salesTokens.panelTitle}>{title}</h2>
        {action}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </div>
);

export const SalesBadge: React.FC<{ status: SalesStatus; isExpired?: boolean; className?: string }> = ({
  status,
  isExpired,
  className = ""
}) => {
  const finalStatus = (status === "DRAFT" && isExpired) ? "EXPIRED" : status;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${
        statusStyles[finalStatus] || "bg-slate-50 text-slate-600 border-slate-200"
      } ${className}`}
    >
      {finalStatus.replace(/_/g, " ")}
    </span>
  );
};

export const SalesTabs: React.FC<{
  tabs: { id: string; label: string }[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
}> = ({ tabs, active, onChange, className = "" }) => (
  <div className={`flex gap-1 border-b border-slate-200 ${className}`}>
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        onClick={() => onChange(tab.id)}
        className={`px-3 py-2 text-[11px] transition-colors -mb-px ${
          active === tab.id ? salesTokens.tabActive : salesTokens.tabIdle
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

export const SalesLoading: React.FC<{ message?: string }> = ({
  message = "Loading…"
}) => (
  <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3 text-slate-500">
    <Loader2 className="w-8 h-8 text-ctb-blue animate-spin" strokeWidth={2} />
    <p className="text-[11px] font-medium tracking-wide">{message}</p>
  </div>
);

export const SalesBackLink: React.FC<{
  to: string;
  label?: string;
  onClick?: () => void;
}> = ({ to, label = "Back to leads", onClick }) => {
  const inner = (
    <>
      <ArrowLeft size={14} className="shrink-0" />
      <span>{label}</span>
    </>
  );
  const cls =
    "inline-flex items-center gap-1.5 text-[11px] font-medium text-ctb-blue-600 hover:text-ctb-blue transition";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  );
};

type BtnVariant = "primary" | "secondary" | "ghost" | "success" | "danger";

const btnVariants: Record<BtnVariant, string> = {
  primary:
    "bg-ctb-blue text-white border-ctb-blue hover:bg-ctb-blue/90 shadow-sm shadow-ctb-blue/15",
  secondary:
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300",
  ghost: "bg-transparent text-slate-600 border-transparent hover:bg-slate-100",
  success:
    "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-sm",
  danger:
    "bg-rose-600 text-white border-rose-600 hover:bg-rose-700 shadow-sm"
};

export const SalesButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: BtnVariant;
    size?: "sm" | "md";
  }
> = ({ variant = "primary", size = "sm", className = "", children, ...props }) => {
  const sizeCls = size === "md" ? "px-4 py-2 text-xs" : "px-3 py-1.5 text-[11px]";
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg border transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ${sizeCls} ${btnVariants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const SalesLinkButton: React.FC<
  { to: string; variant?: BtnVariant; size?: "sm" | "md"; className?: string; children: React.ReactNode }
> = ({ to, variant = "secondary", size = "sm", className = "", children }) => {
  const sizeCls = size === "md" ? "px-4 py-2 text-xs" : "px-3 py-1.5 text-[11px]";
  return (
    <Link
      to={to}
      className={`inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg border transition ${sizeCls} ${btnVariants[variant]} ${className}`}
    >
      {children}
    </Link>
  );
};

export const SalesInsightBanner: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => (
  <div className="rounded-xl border border-ctb-blue-200/50 bg-gradient-to-r from-ctb-blue-0/30 to-white px-4 py-3">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-ctb-blue mb-1">{title}</p>
    <div className="text-[11px] text-slate-600 leading-relaxed">{children}</div>
  </div>
);

export const SalesMetricTile: React.FC<{
  label: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ label, children, footer }) => (
  <div className={`${salesTokens.statCard} flex flex-col justify-between min-h-[88px]`}>
    <div className={salesTokens.statAccent} />
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 pl-1">{label}</span>
    <div className="pl-1 mt-1">{children}</div>
    {footer && <div className="text-[10px] text-slate-400 mt-2 pl-1">{footer}</div>}
  </div>
);
